<template>
  <a-modal
    v-model="visible"
    :title="exportInfo.title"
    :destroyOnClose="true"
    @cancel="closeModal"
  >
    <div class="export-modal">
      <span v-if="step === 1 && !loading" v-html="exportInfo.default_text" class="font-size-16" />
      <div v-if="loading" class="d-flex flex-column">
        <div class="d-flex flex-column mb-3">
          <FormTitle title="Generating file" />
          <span>This may take a few moments</span>
        </div>
        <LottieAnimation
          style="height: 30px;"
          ref="anim"
          :animationData="require('@/components/custom/modals/export/jsonAnim/newLoader.json')"
          :loop="true"
          :autoPlay="true"
        />
      </div>
      <div v-if="step === 2 && !loading">
        <div class="d-flex flex-column mb-3">
          <FormTitle title="Your file is ready" />
          <span class="d-block font-size-16 mb-2">The CSV export for current launch statistics is ready. Click the link below to download it.</span>
          <a
            :href="downloadLink" :download="`${exportModalTarget}_file`"
            class="font-size-16 text-primary"
            style="width: fit-content;"
          >
            <a-icon type="link" />
            Download link
          </a>
        </div>
      </div>
    </div>
    <template slot="footer">
      <a-button @click="closeModal">
        {{ step !== 2 ? 'Cancel' : 'Close' }}
      </a-button>
      <a-button
        v-if="step !== 2"
        :type="actionButton.type"
        :loading="loading"
        :disabled="loading"
        @click="action"
      >
        {{ actionButton.text }}
      </a-button>
    </template>
  </a-modal>
</template>

<script>
import apiClient from '@/services/axios'
import FormTitle from '@/components/custom/form/FormTitle.vue'
import LottieAnimation from 'lottie-web-vue'
import { getErrorMessage } from '@/views/apps/fortune-wheel/composables/useGetErrorMessage.js'

const wheelApiUrl = process.env.VUE_APP_WHEEL_API_URL

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    exportInfo: {
      type: Object,
      default: () => {},
    },
    exportModalTarget: {
      type: String,
      default: '',
    },
    actionButton: {
      type: Object,
      default: () => ({
        text: 'Confirm',
        type: 'primary',
      }),
    },
  },
  components: {
    FormTitle,
    LottieAnimation,
  },
  data: () => ({
    step: 1,
    downloadLink: '',
    loading: false,
    visible: false,
  }),
  watch: {
    show: {
      handler(val) {
        if (val) {
          this.visible = true
        } else {
          this.visible = false
          this.step = 1
        }
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    getPopupContainer(trigger) {
      return trigger.parentElement
    },
    createFileLink(response) {
      const blob = new Blob([response.data], { type: response.headers['content-type'] })
      this.downloadLink = URL.createObjectURL(blob)
    },
    sleep(ms = 2000) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },
    async action() {
      try {
        this.loading = true
        const wheelId = this.$route.params.id
        const url = `${wheelApiUrl}/admin/logs/${wheelId}/export`
        const response = await apiClient.get(url, {
          responseType: 'blob',
          params: { block_type: this.exportModalTarget },
        })
        if (response?.status >= 200 && response?.status < 300) {
          await this.sleep()
          this.createFileLink(response)
          this.step = 2
          return
        }

        getErrorMessage(response?.data)
      } catch (e) {
        this.$notification.error({
          message: e?.message ? e.message : 'Error',
        })
      } finally {
        this.loading = false
      }
    },
    closeModal() {
      this.$emit('close')
    },
  },
}
</script>

<style lang="scss">
.export-modal {
  display: flex;
  justify-content: center;
  flex-direction: column;
  & .ant-upload-list-item {
    height: auto;
    padding: 5px;
    display: flex;
    align-items: center;
    border: 1px solid #e4e9f0;
    border-radius: 4px;
  }
  & .ant-upload-list-item-info {
    display: flex;
    justify-content: center;
    align-items: center;
    height: auto;
    padding: 0;
    & > span {
      display: flex;
      align-items: center;
    }
    & .anticon-paper-clip {
      top: 50%;
      transform: translateY(-50%);
    }
  }
  & .ant-upload-list-item:hover .ant-upload-list-item-info {
    background-color: transparent;
  }
  &__empty {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100px;
    max-height: 100px;
  }
  &__info {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 20px 0 0;
    gap: 10px;
    &__icon {
      display: flex;
      align-self: flex-start;
      margin-top: 3px;
    }
  }
}
</style>
